import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { ButtonType, ButtonVariant, CardIcon, useModal } from '@ampol/design-system';
import EditMultipleCard from '../edit-multiple-card/edit-multiple-card';
import AddEditCardImage from '../add-edit-card-image/add-edit-card-image';
import { getCardDetailRequest, editMultipleCardDetails, setMultipleCardStatus, resetUpdateMultipleCardDetails, resetCardDetails } from 'actions/cardActions';
import { getPlasticType, resetPlasticType } from 'actions/maintenanceActions';
import { useSelector, useDispatch } from 'react-redux';
import './edit-multiple-card-wrapper.scss';
import ConfirmExitModal from 'features/cards/modals/confirm-exit-modal/confirm-exit-modal';
import { switchExpiryDate } from 'utilities/card-utilities';
import { getAddEditCardRequest, getEditCardDetails, getFleetLimiterCodesObj } from 'features/cards/helpers/add-edit-card-helper';
import Label from 'constants/labels';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Wrapper for edit multiple cards modal used in the 'Update Status > Update Card Details Before Reissue flow.'
 *  
 * @param props
 */


const EditMultipleCardWrapper = (props) => {
  const [cardPageNumber, setCardPageNumber] = useState(1);
  const [cardList, setCardList] = useState([]);
  const [selectedCard, setSelectedCard] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [showConfirmExitModal, setShowConfirmExitModal] = useState(false);
  const [modifiedCardDetails, setModifiedCardDetails] = useState({});
  
  const dispatch = useDispatch();
  const cardDetail = useSelector((state) => state.cardDetailView.cardDetail);
  const cardDetailLoading = useSelector((state) => state.cardDetailView.isLoading);
  const updateCardStatus = useSelector((state) => state.editMultipleCardDetail.isLoading);
  const updateCardSuccess = useSelector((state) => state.editMultipleCardDetail.success);
  const plasticType = useSelector((state) => state.maintenanceStore.plasticType);
  const plasticTypeLoaded = useSelector((state) => state.maintenanceStore.plasticTypeLoaded);
  const assignedGrandfatherClientNumber = useSelector((state) => state.userStore.assignedGrandfatherClientNumber);
  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const assignedAccountNumber = useSelector((state) => state.userStore.assignedAccountNumber);

  const isConfirmScreen = props?.cardDetailsList.length + 1 === cardPageNumber ;

  useEffect(() => {
    if (updateCardSuccess) preCloseModalFn(); 
  }, [updateCardSuccess]);
  

  useEffect(() => {
    if (cardDetail) {
      setSelectedCard(cardDetail);
      if (cardDetail.plasticTypeCode) dispatch(getPlasticType(cardDetail.plasticTypeCode));
    }
  }, [cardDetail]);

  useEffect(() => {
    if (props && props.cardDetailsList.length > 0) {
      formModal.open();
    }

    return () =>{
      dispatch(resetUpdateMultipleCardDetails());
      dispatch(resetCardDetails());
      dispatch(resetPlasticType());
    };
  }, []);

  // mapping product and services string array to actual product and service array(from api)
  const productServiceMapping = (editedServices, originalServices) =>{
    if (!editedServices || !originalServices) return originalServices ?? [];

    return originalServices.map((card) => {
      card.selected = editedServices.some((desc) => desc === card.desc);

      return card;
    });
  };

  // On page number change
  useEffect(() => {
    setModifiedCardDetails({});
    if (props.cardDetailsList.length> 0 && !isConfirmScreen){
      const cardProp = props.cardDetailsList[cardPageNumber - 1];
      // card details already present then do not call api
      if (cardList.some((card) =>  card.updated.contractIdentifier === cardProp?.cardNumber)) {
        const existingCard = cardList.find((card) =>  card.updated.contractIdentifier === cardProp?.cardNumber);
        const cardDetails = { ...existingCard.original, ...existingCard.updated, fleetLimiters: productServiceMapping(existingCard.updated.services, existingCard.original.services) };
        setSelectedCard(cardDetails);
      } else 
      {
        dispatch(getCardDetailRequest(cardProp?.cardNumber, props.grandParentAccountNumber));
      }
    }
    //reset card details if user on confirm screen
    if (isConfirmScreen) setSelectedCard({});
      
  }, [cardPageNumber]);
  
  // call update cards multiple api if card details updated
  const onSubmitCardChanges = ()=>{
    const request = {
      cardStatus: props.cardStatus,
      cards: cardList.map((card) => card.updated),
      grandParentAccountNumber: props.grandParentAccountNumber
    };

    if (request.cards && request.cards.length > 0){
      dispatch(editMultipleCardDetails(request, props.grandParentAccountNumber));
      formModal.close();
      // call set multiple card status api for those card details not updated
      // setMultipleCardStatusAction(cardList.filter((c) => !c.updated.isModified).map((card) => card.updated.contractIdentifier), false);
      trackCustomEvent('Submit Edit Card and Update Status', loggedInUser, assignedAccountNumber, 'Clicked Submit to submit multiple Edit Card changes and Card Status update.', 
        { selectedCard: request.cards }
      );
    }
  };

  const setMultipleCardStatusAction = (cardList, closeModal = true) => {
    const setStatusRequest = {
      cardsList: cardList?.join(';') ?? props.cardDetailsList.map(card => card.cardNumber).join(';'),
      cardStatusSelected: props.cardStatus,
    };
    if (setStatusRequest.cardsList){
      dispatch(setMultipleCardStatus(setStatusRequest.cardsList, setStatusRequest.cardStatusSelected, assignedGrandfatherClientNumber));
      trackCustomEvent('Submit Update Status', loggedInUser, assignedAccountNumber, 'Clicked Confirm And Exit to submit Card Status update without Edit Card changes.', 
        { selectedCard: setStatusRequest.cardsList }
      );
    }

    if (closeModal) {
      // refresh cards data on close of edit multiple modal close
      if (setStatusRequest.cardsList?.length > 0) {
        props.getCardsRequest?.();// this will update card detail and status in cards table 
        props.resetCardSelection?.(); 
      }
      setShowConfirmExitModal(false);
      formModal.close();
      props.displayUpdateCardDetailsModal?.(false);
    }
  };

  /**
   * Called on submit of each card in the edit multiple card flow. 
   * Builds or edits the request object in cardList
   * @param {Object} changes Updated card details submitted on continue of edit multiple card modal. 
   */
  const handleCardChanges = (changes)=>{
    // Get original card details.
    const originalCard = cardList.find((item) => item.original && item.original.cardNumber === selectedCard.contractIdentifier); // If it already exists in the list, use it.
    const selectedCardDetails = originalCard ? originalCard.original : selectedCard;

    // Map original card details to the same format as submitted changes object
    const selectedCardDetailsTrimmed = getAddEditCardRequest(
      {
        ...getEditCardDetails(selectedCardDetails),
        ...getFleetLimiterCodesObj(selectedCardDetails?.fleetLimiters),
      },
      selectedCardDetails.fleetLimiters,
      assignedGrandfatherClientNumber
    );

    // Checks whether card details have been changed
    const isNotModifed = _.isEqual(
      _.omit(changes, ['contractIdentifier', 'contractSearchMethod', 'plasticType', 'grandParentAccountNumber']),
      _.omit(selectedCardDetailsTrimmed, ['contractSearchMethod', 'plasticType', 'contractIdentifier', 'grandParentAccountNumber',])
    );

    const editcardDetails = { ...changes, isModified: !isNotModifed, setCardStatus: props.cardStatus };

    if (cardList.some((card) => card.updated.contractIdentifier === changes.contractIdentifier)) {
      const updatedList = cardList.map((card) => {
        if (card.updated.contractIdentifier === changes.contractIdentifier) {
          return { original: selectedCardDetails, updated: editcardDetails };
        }
        return card;
      });

      setCardList(updatedList);
    } else {
      setCardList([...cardList, { updated: editcardDetails, original: selectedCardDetails }]);
    }
    setCardPageNumber(cardPageNumber + 1);
  };

  const preCloseModalFn = () =>{
    if (updateCardSuccess){
      props.getCardsRequest?.();
      props.resetCardSelection?.();
      setShowConfirmExitModal(false);
      props.displayUpdateCardDetailsModal?.(false);
      return ;
    }

    setShowConfirmExitModal(true);
  };

  const setIsFormValidfn = (isValid) =>{
    if ( isValid !== isFormValid) setIsFormValid(isValid);
  };

  const formModal = useModal({
    id: 'edit-card-form-details',
    title: isConfirmScreen ? Label.EDIT_CARD : `Edit Card ${cardPageNumber} of ${props.cardDetailsList.length}`,
    subtitle: isConfirmScreen ? 'Confirm Changes' : selectedCard?.cardNumber ?? '',
    preCloseFn: () => preCloseModalFn(),
    manualClose: true,
    content: <EditMultipleCard 
      onClose={() => formModal.close()}
      cardDetail={selectedCard}
      isConfirmScreen = {isConfirmScreen}
      cardDetailLoading={cardDetailLoading}
      cardList={isConfirmScreen ? cardList.map((card) => card.updated): undefined}
      onCardChanges={!isConfirmScreen ? handleCardChanges: undefined}
      setIsFormValid={setIsFormValidfn}
      setModifiedCardDetails={setModifiedCardDetails}
    />,
    variant: 'double',
    color: 'blue',
    actions: [
      {
        label: isConfirmScreen ? 'Submit' : 'Continue',
        variant: ButtonVariant.Primary ,
        type: ButtonType.Submit,
        ...(isConfirmScreen && !(updateCardStatus || updateCardSuccess)) && { onClick: () => onSubmitCardChanges() },
        inactive: (!isConfirmScreen && !isFormValid) || updateCardStatus || updateCardSuccess || cardDetailLoading,
      },
      {
        label: 'Back',
        variant: ButtonVariant.Secondary,
        inactive: cardPageNumber === 1 || (updateCardStatus || updateCardSuccess) || cardDetailLoading,
        ...(cardPageNumber > 1 && !(updateCardStatus || updateCardSuccess)) && { onClick: () => setCardPageNumber(cardPageNumber - 1) },
      },
    ],
    formId: `edit-multiple-card-${selectedCard?.cardNumber}`,
    icon: plasticTypeLoaded && !isConfirmScreen && plasticType?.image?.url ? <CardIcon className='edit-multiple-card-wrapper--icon' url={plasticType?.image?.url} onClick={()=> cardImageModal.open()} /> : <></>,
  });

  const getCardDetails = (cardDetails) =>{
    return {
      embossingLine2: cardDetails?.embossedCompanyName ?? '',
      embossingName:  cardDetails?.embossedLastName ?? '', 
      regoNumber: cardDetails?.registrationNumber ?? '', 
      expiryDate: cardDetails?.expiryDate ? cardDetails?.expiryDate?.length === 4 ? switchExpiryDate(cardDetails?.expiryDate): cardDetails?.expiryDate : '',
    };
  };
  const cardImageModal = useModal({
    id: 'card-image-modal',
    title: `Edit Card ${cardPageNumber} of ${props.cardDetailsList.length}`,
    subtitle: selectedCard?.cardNumber ?? '',
    variant: 'double',
    color: 'blue',
    content: <AddEditCardImage plasticType={plasticType} {...getCardDetails({ ...selectedCard, ...modifiedCardDetails })} />,
    actions: [
      {
        label: 'Back',
        onClick: () => cardImageModal.close(),
      },
    ],
  });

  return <div>
    {showConfirmExitModal && <ConfirmExitModal 
      title={'Are you sure?'}
      content={'Leaving now will set the status without modifying any card details.'}
      onConfirmExitClick={setMultipleCardStatusAction}
      onClose={() => setShowConfirmExitModal(false)}
    />}
  </div>;
};

export default EditMultipleCardWrapper;
